import { PropTypes } from 'prop-types';
import { Typography, Tooltip, DialogContent, DialogActions, IconButton, Card, CardMedia, Button, CardContent, Grid, Dialog, DialogTitle, CardActions } from '@mui/material';
import { Image as ImageIcon, Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetOrganizationUserQuery } from '../features/api/rest/apiOrganizationsSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import { useDestroyOrganizationProductMutation } from '../features/api/rest/apiProductOrganizationSlice';
import { useGetProductQuery } from '../features/api/openFoodFacts/apiFoodFactsSlice';
import ProductOrganizationBoardDialog from './ProductOrganizationBoardDialog';
import ProductOrganizationDialog from './ProductOrganizationDialog';

function ProductOrganizationCard({ ean, name, brands = [{}] }) {
  const { opk } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProduct] = useDestroyOrganizationProductMutation();
  const { data: profile } = useGetProfileQuery();
  const { data: users = [] } = useGetOrganizationUserQuery(opk);
  const { data: product, isLoading: isLoadingProduct, isFetching: isFetchingProduct, isError: isErrorProduct } = useGetProductQuery(ean);

  let SystemAdmin = false;
  let OrgAdmin = false;
  const permission = () => {
    if (profile.is_staff) {
      SystemAdmin = true;
    }
    OrgAdmin = users.some((user) => user.is_admin && profile.id === user.user);
    if (OrgAdmin || SystemAdmin) return true;
    return false;
  };
  const goToProduct = () => {
    if (permission()) navigate(`/profile/organizations/${opk}/products/${ean}`);
    else navigate(`/profile/organizations/${opk}/products/${ean}/contents`);
  };
  const cardMediaImage = <CardMedia sx={{ height: '10em', width: '100%', objectFit: 'contain' }} component="img" image={product?.product?.image_front_small_url} alt={t('products.research.imageAlt')} />;
  const cardMediaPlaceHolder = (
    <CardMedia sx={{ height: '10em', width: '100%', objectFit: 'contain', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'text.secondary' }}>
      <ImageIcon sx={{ color: 'text.tertiary', fontSize: '2.5rem' }} />
    </CardMedia>
  );
  const handleDelete = () => {
    deleteProduct({ opk, ean });
    setIsDeleting(false);
  };
  return (
    <>
      <Card className="Product_Card">
        <Tooltip title={t('organizations.product.goToProduct')} placement="top">
          <IconButton onClick={goToProduct} sx={{ position: 'absolute', top: 1, right: 1 }}>
            <ArrowForwardIcon />
          </IconButton>
        </Tooltip>
        <CardContent sx={{ p: 1 }} onClick={goToProduct}>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {((isLoadingProduct || isFetchingProduct || isErrorProduct || !product?.product?.image_front_small_url) && cardMediaPlaceHolder) || cardMediaImage}
            <Typography
              variant="h7"
              sx={{
                paddingTop: '1em',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                textAlign: 'initial',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {name}
            </Typography>
            <Grid
              container
              sx={{
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
                textAlign: 'initial',
                overflow: 'hidden',
                wordBreak: 'break-word',
                minHeight: '3em',
              }}
            >
              {brands &&
                brands.map((brand) => (
                  <Typography key={brand.id} variant="h8" sx={{ fontSize: '0.9em', pr: '0.6em', color: '#47484e', fontStyle: 'italic' }}>
                    {brand.name}
                  </Typography>
                ))}
            </Grid>
          </Grid>
        </CardContent>
        {permission() && (
          <CardActions sx={{ m: 0, p: 0 }}>
            <Grid container alignItems="center" sx={{ gap: 2, display: 'flex', justifyContent: 'space-evenly' }}>
              <Grid item>
                <Tooltip title={t('common.actions.edit')} placement="top">
                  <IconButton onClick={() => setIsEditing(true)}>
                    <EditIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title={t('organizations.product.board.new')} placement="top">
                  <IconButton onClick={() => setIsAdding(true)}>
                    <AddIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title={t('common.actions.delete')} placement="top">
                  <IconButton onClick={() => setIsDeleting(true)}>
                    <DeleteIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardActions>
        )}
      </Card>
      <Dialog open={isDeleting} onClose={() => setIsDeleting(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>{t('organizations.product.delete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mx={4}>
            {t('organizations.product.deleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
          <Grid display="flex" justifyContent="flex-end" gap={1}>
            <Button type="submit" onClick={() => setIsDeleting(false)} variant="outlined" color="secondary">
              {t('common.actions.close')}
            </Button>
            <Button type="submit" onClick={handleDelete} variant="contained" color="warning">
              {t('common.actions.delete')}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
      <ProductOrganizationBoardDialog ean={ean} actionType="add" isOpen={isAdding} setIsOpen={setIsAdding} />
      <ProductOrganizationDialog actionType="edit" isOpen={isEditing} setIsOpen={setIsEditing} oldEan={ean} oldName={name} />
    </>
  );
}

ProductOrganizationCard.propTypes = {
  ean: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  brands: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

export default ProductOrganizationCard;
